(() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__bcgiftRecommendationsLoaded) return;
  window.__bcgiftRecommendationsLoaded = true;

  function formatMoney(value, currency = 'CAD') {
    const amount = Number(value || 0);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  async function readJson(response) {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.ok === false) {
      throw new Error(payload?.error || `Request failed: ${response.status}`);
    }
    return payload;
  }

  function localizedProductPath(handle) {
    if (!handle || handle === '#') return '#';
    const rootUrl = window.Shopify?.routes?.root || '/';
    const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    return `${normalizedRoot}products/${encodeURIComponent(handle)}`;
  }

  function isChineseLocale() {
    const locale = document.documentElement.lang || window.Shopify?.language || '';
    return /^zh(?:-|$)/i.test(locale);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  const ZH_COPY = new Map([
    ['Recommended for you', '为你推荐'],
    ['Popular with BCgift shoppers', 'BCgift 顾客热门之选'],
    ['Use your credit on these picks', '可使用会员积分的推荐'],
    ['Eligible products that fit your current balance', '适合你当前会员积分余额的商品'],
    ['More in Wellness', '更多健康好物'],
    ['Best matches in this collection', '本系列中更适合你的选择'],
    ['Best match for your budget level', '符合你预算范围的推荐'],
    ['Curated for your shopping range', '根据你的购物预算精选'],
    ['Use your credit in this collection', '本系列可使用会员积分的商品'],
    ['Eligible picks for your current balance', '适合你当前会员积分余额的选择'],
    ['Add a few more picks', '再看看这些推荐'],
    ['Small add-ons for this cart', '适合与购物车商品一起购买的小件好物'],
    ['Add items eligible for credit', '添加可使用会员积分的商品'],
    ['Good picks for your current balance', '适合你当前会员积分余额的推荐'],
    ['Complete your order', '完善你的订单'],
    ['Small add-ons that pair well with this cart', '与购物车商品搭配的精选好物'],
    ['Eligible to use with your member credit', '可使用会员积分'],
    ['Fits this shopping range well', '符合当前购物预算'],
    ['Aligned with the categories this page is focused on', '符合本页面的商品类别'],
    ['Recommended for this shopping moment', '适合本次购物的推荐'],
    ['A curated set of BCgift favorites to get started', '从 BCgift 精选热门商品开始探索'],
    ['Good places to start while your account builds more shopping history', '在累积更多购物记录前，可先从这些热门商品开始'],
  ]);

  function localizeCopy(value) {
    if (!isChineseLocale() || !value) return value || '';
    if (ZH_COPY.has(value)) return ZH_COPY.get(value);
    return String(value)
      .replace(/^Recommended for you in .+$/i, '为你推荐')
      .replace(/^Top picks in .+$/i, '热门推荐')
      .replace(/^Add more from .+$/i, '添加更多推荐商品')
      .replace(/^You also shop .+$/i, '你可能也喜欢');
  }

  const localizedTitleCache = new Map();
  async function localizedProductTitle(item) {
    const fallback = item?.title || '商品';
    if (!isChineseLocale() || !item?.handle) return fallback;
    if (localizedTitleCache.has(item.handle)) return localizedTitleCache.get(item.handle);
    const request = fetch(`${localizedProductPath(item.handle)}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'force-cache',
    }).then((response) => response.ok ? response.json() : null)
      .then((product) => product?.title || fallback)
      .catch(() => fallback);
    localizedTitleCache.set(item.handle, request);
    return request;
  }

  function renderCard(item) {
    const title = item?.localizedTitle || item?.title || (isChineseLocale() ? '商品' : 'Product');
    const handle = item?.handle || '#';
    const image = item?.featuredImage || '';
    const reason = localizeCopy(item?.recommendationReasonLabel || item?.recommendationReason || '');
    const price = formatMoney(item?.price, item?.currency || 'CAD');
    return `
      <a class="bcgift-rec-card" href="${escapeHtml(localizedProductPath(handle))}">
        ${image ? `<img class="bcgift-rec-card__image" src="${escapeHtml(image)}" alt="${escapeHtml(title)}">` : `<div class="bcgift-rec-card__image"></div>`}
        <div class="bcgift-rec-card__meta">
          <h3 class="bcgift-rec-card__title">${escapeHtml(title)}</h3>
          ${reason ? `<p class="bcgift-rec-card__reason">${escapeHtml(reason)}</p>` : ''}
          ${price ? `<p class="bcgift-rec-card__price">${escapeHtml(price)}</p>` : ''}
        </div>
      </a>
    `;
  }

  function renderBlock(block) {
    const items = Array.isArray(block?.items) ? block.items : [];
    if (!items.length) return '';
    const title = localizeCopy(block?.title || '');
    const subtitle = localizeCopy(block?.subtitle || '');
    return `
      <section class="bcgift-rec-block" data-block-key="${escapeHtml(block?.key || '')}">
        <div class="bcgift-rec-block__header">
          ${title ? `<h2>${escapeHtml(title)}</h2>` : ''}
          ${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ''}
        </div>
        <div class="bcgift-rec-grid">
          ${items.map(renderCard).join('')}
        </div>
      </section>
    `;
  }

  async function fetchRecommendations(root) {
    const endpoint = root.dataset.endpoint;
    const customerGid = root.dataset.customerGid || '';
    const collectionHandle = root.dataset.collectionHandle || '';
    const surface = root.dataset.surface || '';
    const locale = root.dataset.locale || document.documentElement.lang || window.Shopify?.language || '';
    const url = new URL(endpoint, window.location.origin);
    if (locale) {
      url.searchParams.set('locale', locale);
    }
    if (customerGid) {
      url.searchParams.set('customer_gid', customerGid);
    }
    if (collectionHandle) {
      url.searchParams.set('collection_handle', collectionHandle);
    }

    if (surface === 'cart') {
      const payloadNode = root.querySelector('[data-bcgift-cart-recommendations-payload]');
      let payload = {};
      try {
        payload = JSON.parse(payloadNode?.textContent || '{}');
      } catch (_error) {
        payload = {};
      }
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      return readJson(response);
    }

    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    return readJson(response);
  }

  async function initRoot(root) {
    const loadingNode = root.querySelector('[data-recommendations-loading]');
    const blocksNode = root.querySelector('[data-recommendation-blocks]');
    try {
      const payload = await fetchRecommendations(root);
      const blocks = Array.isArray(payload?.blocks) ? payload.blocks : [];
      if (isChineseLocale()) {
        await Promise.all(blocks.flatMap((block) => (block.items || []).map(async (item) => {
          item.localizedTitle = await localizedProductTitle(item);
        })));
      }
      const html = blocks.map(renderBlock).filter(Boolean).join('');
      if (!html) {
        if (loadingNode) loadingNode.hidden = true;
        root.hidden = true;
        root.classList.add('hidden');
        root.style.display = 'none';
        return;
      }
      root.hidden = false;
      root.classList.remove('hidden');
      root.style.display = '';
      blocksNode.innerHTML = html;
      blocksNode.hidden = false;
      if (loadingNode) loadingNode.hidden = true;
    } catch (_error) {
      root.hidden = true;
    }
  }

  function scan() {
    document.querySelectorAll('[data-bcgift-recommendations-root]').forEach((root) => {
      if (root.dataset.bcgiftRecommendationsBound === 'true') return;
      root.dataset.bcgiftRecommendationsBound = 'true';
      initRoot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
})();
